const DAY_IN_MS = 24 * 60 * 60 * 1000;

/** Shown when date parsing fails; UI should treat as invalid and show fallback. */
export const INVALID_DATE_LABEL = 'Invalid date';

/** Uses user's local date (YYYY-MM-DD) for Today/Yesterday comparison */
const getLocalDayKey = (value: Date): string => {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Formats date in user's local timezone (no timeZone = browser/device local) */
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
});

/** Formats time in user's local timezone */
const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Date only: "Feb 16" (month short + day, no weekday). */
const dateShortFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

/**
 * Unified date/time formatter
 * Formats dates as "Today HH:mm", "Yesterday HH:mm", or "Day, Mon DD HH:mm"
 */
export const formatDateTime = (dateInput: string | Date): string => {
  const date =
    typeof dateInput === 'string'
      ? new Date(dateInput)
      : new Date(dateInput.getTime());

  if (Number.isNaN(date.getTime())) {
    return INVALID_DATE_LABEL;
  }

  const now = new Date();
  const isToday = getLocalDayKey(date) === getLocalDayKey(now);
  const yesterday = new Date(now.getTime() - DAY_IN_MS);
  const isYesterday = getLocalDayKey(date) === getLocalDayKey(yesterday);

  const datePart = isToday
    ? 'Today'
    : isYesterday
    ? 'Yesterday'
    : dateFormatter.format(date);

  const timePart = timeFormatter.format(date);

  return `${datePart} ${timePart}`;
};

/**
 * Date only: "Feb 16" (no weekday). For match row center.
 */
export const formatDateShort = (dateInput: string | Date): string => {
  const date =
    typeof dateInput === 'string'
      ? new Date(dateInput)
      : new Date(dateInput.getTime());
  if (Number.isNaN(date.getTime())) return '';
  return dateShortFormatter.format(date);
};

/**
 * Format only the time part as HH:mm (24h).
 *
 * @param dateInput - Date string or Date object
 * @returns Time string e.g. "15:00"
 */
export const formatTimeOnly = (dateInput: string | Date): string => {
  const date =
    typeof dateInput === 'string'
      ? new Date(dateInput)
      : new Date(dateInput.getTime());

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return timeFormatter.format(date);
};

/**
 * Extract time-only (HH:mm) from a string produced by formatDateTime (e.g. "Today 15:00" → "15:00").
 * Returns '' when the formatted string is invalid (e.g. "Invalid date" → last word "date" is not a time).
 */
export const getTimeOnlyFromFormatted = (formatted: string): string => {
  if (!formatted || formatted === INVALID_DATE_LABEL) return '';
  const parts = formatted.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? '';
  const looksLikeTime = /^\d{1,2}:\d{2}(:\d{2})?$/.test(last);
  return looksLikeTime ? last : '';
};

/**
 * True if the string looks like a display time from formatDateTime (e.g. "Today 15:00" or "15:00").
 */
export const isValidGameTimeDisplay = (s: string): boolean => {
  if (!s || s === INVALID_DATE_LABEL) return false;
  if (s.includes('T')) return true;
  const timePart = s.trim().split(/\s+/).pop() ?? '';
  return /^\d{1,2}:\d{2}(:\d{2})?$/.test(timePart);
};

/**
 * Normalize game.time for display: if ISO (contains 'T'), format as "Today 15:00"; else use as-is.
 */
export const formatGameTimeForDisplay = (timeStr: string): string => {
  if (!timeStr) return '';
  if (timeStr.includes('T')) return formatDateTime(timeStr);
  return timeStr;
};

/**
 * Parse a game's date/time into a Date for display. Handles date-only (YYYY-MM-DD) as local to avoid
 * timezone issues (new Date("2025-02-23") parses as UTC midnight, wrong in some zones).
 * Used by CreatePredictingStep and any component that needs correct date/time for today's games.
 */
export const parseGameDateForDisplay = (game: {
  date: string;
  time: string;
  rawDate?: string;
}): Date | null => {
  const { date, time, rawDate } = game;
  const timePart = getTimeOnlyFromFormatted(time);
  const parseDateOnly = (s: string, hh = 12, mm = 0): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s).trim());
    if (!match) return null;
    const [, y, m, d] = match.map(Number);
    const parsed = new Date(y, m - 1, d, hh, mm);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  if (time?.includes('T')) {
    const parsed = new Date(time);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (rawDate) {
    if (rawDate.includes('T')) {
      const parsed = new Date(rawDate);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const [hh, mm] = timePart ? timePart.split(':').map(Number) : [12, 0];
    return parseDateOnly(rawDate, hh ?? 12, mm ?? 0);
  }
  if (date) {
    const [hh, mm] = timePart ? timePart.split(':').map(Number) : [12, 0];
    return parseDateOnly(date, hh ?? 12, mm ?? 0);
  }
  return null;
};

/** 12h time formatter for match header (e.g. "12:30 PM"). */
const matchHeaderTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/**
 * Format for compact match header: "Sun Feb 22 • 12:30 PM".
 * Prefers timeStr if it looks like ISO; otherwise uses dateStr + timeStr.
 */
export const formatMatchHeaderDateTime = (dateStr: string, timeStr: string): string => {
  const iso = timeStr?.includes('T') ? timeStr : dateStr && timeStr ? `${dateStr}T${timeStr}` : dateStr || timeStr;
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const datePart = dateFormatter.format(date);
  const timePart = matchHeaderTimeFormatter.format(date);
  return `${datePart} • ${timePart}`;
};
